import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { 
  PhoneIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  PlayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

import { callService } from '../../services/callService';
import { campaignService } from '../../services/campaignService';
import { Call, CallFilters, CallStatus } from '../../types/call';
import { Campaign } from '../../types/campaign';
import { formatLeadCarrier, formatLeadLineType } from '../../utils/leadLookupDisplay';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Breadcrumb from '../../components/UI/Breadcrumb';
import { Table, Pagination, Column } from '../../components/UI/Table';
import StatusBadge from '../../components/UI/StatusBadge';
import Button from '../../components/UI/Button';
import Modal from '../../components/UI/Modal';
import { Card, CardContent } from '../../components/UI/Card';
import CallRowActionsMenu from '../../components/Calls/CallRowActionsMenu';



const CallsPage: React.FC = () => {
  const { id: campaignId } = useParams<{ id: string }>();
  const [calls, setCalls] = useState<Call[]>([]);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<CallFilters>({
    search: '',
    page: 1,
    limit: 50
  });
  const [totalCount, setTotalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCall, setSelectedCall] = useState<Call | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [transcript, setTranscript] = useState<string | null>(null);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const [loadingRecording, setLoadingRecording] = useState(false);
  const [showCallDetails, setShowCallDetails] = useState(false);

  const getLeadInfo = (call: Call) => {
    if (call.lead) {
      return {
        fullName: call.lead.full_name || 'Unknown',
        phoneE164: call.lead.phone_e164 || 'No phone'
      };
    } else {
      return {
        fullName: 'Unknown',
        phoneE164: 'No phone'
      };
    }
  };

  const getCallLookup = (call: Call) => {
    const lead = call.lead as
      | (NonNullable<Call['lead']> & { lineType?: string | null; carrierName?: string | null })
      | undefined;
    const extra = call as Call & {
      lead_line_type?: string | null;
      lead_carrier?: string | null;
      lead_carrier_name?: string | null;
    };
    return {
      lineType: lead?.line_type ?? lead?.lineType ?? extra.lead_line_type ?? null,
      carrier: lead?.carrier ?? extra.lead_carrier ?? null,
      carrierName: lead?.carrier_name ?? lead?.carrierName ?? extra.lead_carrier_name ?? null,
    };
  };

  const formatCallDate = (value?: string) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const fetchCampaign = useCallback(async () => {
    if (!campaignId) return;
    
    try {
      const campaignData = await campaignService.getCampaign(campaignId);
      setCampaign(campaignData);
    } catch (error) {
      console.error('Failed to fetch campaign:', error);
    }
  }, [campaignId]);

  // Create a debounced version of fetchCalls to avoid excessive API calls when typing
  const debouncedFetchCalls = useCallback(
    (() => {
      let timeout: NodeJS.Timeout;
      return () => {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
          setLoading(true);
          const fetchPromise = campaignId 
            ? callService.getCalls(campaignId, filters)
            : callService.getAllCalls(filters);
          fetchPromise
            .then(response => {
              setCalls(response.calls);
              setTotalCount(response.total);
            })
            .catch(error => {
              console.error('Error fetching calls:', error);
            })
            .finally(() => {
              setLoading(false);
            });
        }, 300);
      };
    })(),
    [campaignId, filters]
  );

  const fetchCalls = useCallback(async () => {
    try {
      setLoading(true);
      let response;
      if (campaignId) {
        response = await callService.getCalls(campaignId, filters);
      } else {
        response = await callService.getAllCalls(filters);
      }
      setCalls(response.calls);
      setTotalCount(response.total);
    } catch (error) {
      console.error('Error fetching calls:', error);
    } finally {
      setLoading(false);
    }
  }, [campaignId, filters]);

  useEffect(() => {
    if (campaignId) {
      fetchCampaign();
    }
    fetchCalls();
  }, [campaignId, fetchCampaign, fetchCalls]);

  const handleSearch = (search: string) => {
    setFilters(prev => ({ ...prev, search, page: 1 }));
    // Use debounced fetch for search to avoid excessive API calls
    debouncedFetchCalls();
  };

  const handleStatusFilter = (value: string) => {
    const callStatuses = new Set<CallStatus>([
      'pending',
      'queued',
      'ringing',
      'in_progress',
      'completed',
      'failed',
      'cancelled',
    ]);
    if (!value) {
      setFilters(prev => ({ ...prev, status: undefined, lead_status: undefined, result: undefined, page: 1 }));
    } else if (callStatuses.has(value as CallStatus)) {
      setFilters(prev => ({ ...prev, status: value as CallStatus, lead_status: undefined, result: undefined, page: 1 }));
    } else {
      setFilters(prev => ({ ...prev, status: undefined, lead_status: undefined, result: value, page: 1 }));
    }
    // Trigger fetch calls with updated filters
    fetchCalls();
  };

  const handleInterestedFilter = (interested: boolean | undefined) => {
    // Note: This filter is no longer used as 'interested' field was removed
    // Keeping the function for UI compatibility but not applying the filter
    console.warn('Interested filter is deprecated - interested field was removed from call attempts');
  };



  const formatDuration = (startedAt: string, finishedAt: string | null) => {
    if (!finishedAt) return 'N/A';
    
    const start = new Date(startedAt);
    const end = new Date(finishedAt);
    const durationMs = end.getTime() - start.getTime();
    const durationSec = Math.floor(durationMs / 1000);
    
    const minutes = Math.floor(durationSec / 60);
    const seconds = durationSec % 60;
    
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatPhoneNumber = (phone: string) => {
    // Simple phone number formatting
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  const normalizeDisposition = (reason?: string): string | null => {
    if (!reason) return null;
    const normalized = reason.toLowerCase().replace(/_/g, ' ').trim();
    if (normalized === 'not interested') return 'not_interested';
    if (normalized === 'follow up') return 'follow_up';
    if (normalized === 'do not call') return 'do_not_call';
    if (normalized === 'out of business') return 'out_of_business';
    if (normalized === 'wrong number') return 'wrong_number';
    if (normalized === 'app in') return 'app_in';
    if (normalized === 'app out') return 'app_out';
    if (normalized === 'no answer / voicemail' || normalized === 'voicemail') return 'voicemail';
    if (normalized === 'no answer') return 'no_answer';
    if (normalized === 'busy') return 'busy';
    if (normalized === 'gatekeeper') return 'gatekeeper';
    if (normalized === 'operator') return 'operator';
    if (normalized === 'deceased') return 'deceased';
    if (normalized === 'spanish') return 'spanish';
    if (normalized === 'interested') return 'interested';
    if (normalized === 'unqualified') return 'unqualified';
    if (normalized === 'unreachable') return 'unreachable';
    if (normalized === 'hang up' || normalized === 'hangup') return 'hangup';
    return null;
  };

  const openTranscriptModal = async (call: Call) => {
    setSelectedCall(call);
    setShowTranscript(true);
    setTranscript(null);
    setRecordingUrl(null);
    
    // Fetch transcript if not already available
    if (call.telnyx_call_control_id && !call.transcript) {
      try {
        setLoadingTranscript(true);
        const transcript = await callService.getCallTranscript(call.id);
        setTranscript(transcript);
      } catch (error) {
        console.error('Error fetching transcript:', error);
        setTranscript(null);
      } finally {
        setLoadingTranscript(false);
      }
    } else {
      setTranscript(call.transcript || null);
    }
    
    // Always fetch recording on open so backend can refresh from Telnyx if stale/expired.
    if (call.telnyx_call_control_id) {
      try {
        setLoadingRecording(true);
        const recordingUrl = await callService.getCallRecording(call.id);
        setRecordingUrl(recordingUrl);
      } catch (error) {
        console.error('Error fetching recording:', error);
        setRecordingUrl(call.recording_url || null);
      } finally {
        setLoadingRecording(false);
      }
    } else {
      setRecordingUrl(call.recording_url || null);
    }
  };

  const playRecording = async (call: Call) => {
    try {
      let url = call.recording_url;
      
      if (call.telnyx_call_control_id) {
        const recordingUrl = await callService.getCallRecording(call.id);
        url = recordingUrl || url;
      }
      
      if (url) {
        window.open(url, '_blank');
      } else {
        toast.error('Recording not available for this call.');
      }
    } catch (error) {
      console.error('Error fetching recording:', error);
      toast.error('Error loading recording.');
    }
  };

  const handleViewCallDetails = (call: Call) => {
    setSelectedCall(call);
    setShowCallDetails(true);
  };

  return (
    <div className="space-y-4">
      {/* Breadcrumb */}
      <Breadcrumb
        items={campaignId ? [
          { label: 'Campaigns', href: '/campaigns' },
          { label: campaign?.name || 'Campaign', href: `/campaigns/${campaignId}` },
          { label: 'Call History' }
        ] : [
          { label: 'Dashboard', href: '/' },
          { label: 'All Calls' }
        ]}
      />

      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-foreground">
          {campaignId ? 'Call History' : 'All Calls'}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {totalCount.toLocaleString()} {totalCount === 1 ? 'call' : 'calls'}
          {campaign?.name ? ` · ${campaign.name}` : ''}
        </p>
      </div>

      {/* Filters + table */}
      <Card className="overflow-hidden">
        <CardContent className="px-4 py-4 sm:px-5">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-4 w-4 text-muted-foreground" />
              </div>
              <input
                type="text"
                value={filters.search || ''}
                onChange={(e) => handleSearch(e.target.value)}
                className="input pl-9 h-9 text-sm"
                placeholder="Search by lead name, phone, or call ID..."
              />
            </div>
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outlined"
              size="sm"
            >
              <FunnelIcon className="-ml-0.5 mr-2 h-4 w-4" />
              Filters
            </Button>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="max-w-xs">
                <label className="block text-sm font-medium text-foreground mb-1">
                  Status
                </label>
                <select
                  value={filters.result || filters.status || ''}
                  onChange={(e) => handleStatusFilter(e.target.value)}
                  className="select"
                >
                  <option value="">All statuses</option>
                  <optgroup label="Dispositions">
                    <option value="interested">Interested</option>
                    <option value="not_interested">Not Interested</option>
                    <option value="follow_up">Follow Up</option>
                    <option value="no_answer">No Answer</option>
                    <option value="voicemail">Voicemail</option>
                    <option value="busy">Busy</option>
                    <option value="app_out">App Out</option>
                    <option value="app_in">App In</option>
                    <option value="out_of_business">Out of Business</option>
                    <option value="unqualified">Unqualified</option>
                    <option value="wrong_number">Wrong Number</option>
                    <option value="gatekeeper">Gatekeeper</option>
                    <option value="operator">Operator</option>
                    <option value="hangup">Hang Up</option>
                    <option value="do_not_call">Do Not Call</option>
                    <option value="deceased">Deceased</option>
                    <option value="spanish">Spanish</option>
                  </optgroup>
                  <optgroup label="Call attempt">
                    <option value="pending">Pending</option>
                    <option value="queued">Queued</option>
                    <option value="ringing">Ringing</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                    <option value="cancelled">Cancelled</option>
                  </optgroup>
                </select>
              </div>
            </div>
          )}
        </CardContent>

        {loading ? (
          <div className="flex items-center justify-center py-16 border-t border-border">
            <LoadingSpinner size="lg" />
          </div>
        ) : calls.length === 0 ? (
          <div className="text-center py-12 border-t border-border">
            <div className="mx-auto h-12 w-12 rounded-full bg-muted flex items-center justify-center">
              <PhoneIcon className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="mt-3 text-sm font-semibold text-foreground">No calls found</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {filters.search || filters.status || filters.result
                ? 'No calls match your current filters.'
                : 'No calls have been made for this campaign yet.'}
            </p>
          </div>
        ) : (
          <Table
            data={calls}
            size="sm"
            className="border-0 rounded-none [&_.table-cell]:!px-3 [&_.table-cell]:!py-2.5 [&_.table-head]:!h-10 [&_.table-head]:!px-3"
            columns={[
              {
                key: 'lead',
                header: 'Lead',
                render: (call) => {
                  const leadInfo = getLeadInfo(call);
                  return (
                    <div className="min-w-[12rem] leading-tight">
                      <div className="text-sm font-medium text-foreground truncate">
                        {leadInfo.fullName}
                      </div>
                      <div className="text-xs text-muted-foreground tabular-nums">
                        {leadInfo.phoneE164 !== 'No phone' ? formatPhoneNumber(leadInfo.phoneE164) : '—'}
                      </div>
                    </div>
                  );
                }
              },
              {
                key: 'lookup',
                header: 'Type / Carrier',
                render: (call: Call) => {
                  const lookup = getCallLookup(call);
                  const lineType = formatLeadLineType(lookup.lineType);
                  const carrier = formatLeadCarrier(lookup.carrierName, lookup.carrier);
                  if (lineType === '—' && carrier === '—') {
                    return <span className="text-xs text-muted-foreground">—</span>;
                  }
                  return (
                    <div className="leading-tight whitespace-nowrap">
                      <div className="text-sm text-foreground">{lineType}</div>
                      <div className="text-xs text-muted-foreground">{carrier}</div>
                    </div>
                  );
                },
              },
              {
                key: 'status',
                header: 'Status',
                render: (call) => (
                  <StatusBadge status={call.status} type="call" />
                )
              },
              ...(campaign?.campaignType === 'bot'
                ? [
                    {
                      key: 'conversationStyle',
                      header: 'Style',
                      render: (call: Call) => (
                        <span className="text-sm text-muted-foreground">
                          {call.conversationStyle?.name || '—'}
                        </span>
                      ),
                    },
                  ]
                : []),
              {
                key: 'amd_result',
                header: 'AMD',
                render: (call) => {
                  if (!call.amd_result) {
                    return <span className="text-xs text-muted-foreground">—</span>;
                  }
                  const amdColors: Record<string, string> = {
                    human: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
                    machine: 'bg-red-500/10 text-red-700 dark:text-red-400',
                    not_sure: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
                    fax: 'bg-muted text-muted-foreground',
                  };
                  return (
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${amdColors[call.amd_result] || 'bg-muted text-muted-foreground'}`}>
                      {call.amd_result === 'not_sure' ? 'Unsure' : call.amd_result.charAt(0).toUpperCase() + call.amd_result.slice(1)}
                    </span>
                  );
                }
              },
              {
                key: 'result',
                header: 'Result',
                render: (call) => {
                  const disposition = normalizeDisposition(call.reason);
                  if (disposition) {
                    return <StatusBadge status={disposition} type="lead" />;
                  }
                  if (call.reason) {
                    return <span className="text-sm text-muted-foreground">{call.reason}</span>;
                  }
                  return <span className="text-xs text-muted-foreground">—</span>;
                }
              },
              {
                key: 'duration',
                header: 'Duration',
                render: (call) => {
                  if (call.duration_seconds && call.duration_seconds > 0) {
                    const minutes = Math.floor(call.duration_seconds / 60);
                    const seconds = call.duration_seconds % 60;
                    return (
                      <span className="text-sm tabular-nums text-foreground">
                        {`${minutes}:${seconds.toString().padStart(2, '0')}`}
                      </span>
                    );
                  }
                  if (call.started_at && call.finished_at) {
                    return (
                      <span className="text-sm tabular-nums text-foreground">
                        {formatDuration(call.started_at, call.finished_at)}
                      </span>
                    );
                  }
                  return <span className="text-xs text-muted-foreground">—</span>;
                }
              },
              {
                key: 'date',
                header: 'Date',
                render: (call) => (
                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                    {formatCallDate(call.started_at || call.created_at)}
                  </span>
                )
              },
              {
                key: 'actions',
                header: '',
                width: '48px',
                align: 'right' as const,
                render: (call) => (
                  <CallRowActionsMenu
                    call={call}
                    onTranscript={() => void openTranscriptModal(call)}
                    onDetails={() => handleViewCallDetails(call)}
                    onRecording={() => void playRecording(call)}
                  />
                )
              }
            ] as Column<Call>[]}
          />
        )}
      </Card>

      {/* Pagination */}
      {totalCount > filters.limit! && (
        <Pagination
          currentPage={filters.page!}
          totalPages={Math.ceil(totalCount / filters.limit!)}
          onPageChange={(page) => setFilters(prev => ({ ...prev, page }))}
          totalItems={totalCount}
          itemsPerPage={filters.limit!}
        />
      )}

      {/* Call Details Modal */}
      <Modal
        isOpen={showCallDetails && !!selectedCall}
        onClose={() => setShowCallDetails(false)}
        title={selectedCall ? `Call Details - ${getLeadInfo(selectedCall).fullName}` : 'Call Details'}
        size="lg"
      >
        {selectedCall && (
          <div>
            <div className="mb-4 p-4 bg-muted rounded-lg">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Call ID:</span> {selectedCall.telnyx_call_control_id || selectedCall.id}
                </div>
                <div>
                  <span className="font-medium">Duration:</span> {selectedCall.started_at && selectedCall.finished_at ? formatDuration(selectedCall.started_at, selectedCall.finished_at) : 'N/A'}
                </div>
                <div>
                  <span className="font-medium">Status:</span> <StatusBadge status={selectedCall.status} type="call" />
                </div>
                <div>
                  <span className="font-medium">Cost:</span> {selectedCall.cost ? `$${selectedCall.cost}` : 'N/A'}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end mt-4">
              <Button
                variant="primary"
                onClick={() => setShowCallDetails(false)}
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Transcript Modal */}
      <Modal
        isOpen={showTranscript && !!selectedCall}
        onClose={() => setShowTranscript(false)}
        title={selectedCall ? `Call Transcript - ${getLeadInfo(selectedCall).fullName}` : 'Call Transcript'}
        size="lg"
      >
        {selectedCall && (
          <div>
            <div className="mb-4 p-4 bg-muted rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Call ID:</span> {selectedCall.telnyx_call_control_id || selectedCall.id}
                  </div>
                  <div>
                    <span className="font-medium">Duration:</span> {selectedCall.started_at && selectedCall.finished_at ? formatDuration(selectedCall.started_at, selectedCall.finished_at) : 'N/A'}
                  </div>
                  <div>
                    <span className="font-medium">Status:</span> <StatusBadge status={selectedCall.status} type="call" />
                  </div>
                  <div>
                    <span className="font-medium">Result:</span> {selectedCall.lead?.status === 'interested' || selectedCall.lead?.status === 'not_interested' ? (
                      <StatusBadge status={selectedCall.lead.status} type="result" />
                    ) : (
                      <span className="text-sm text-muted-foreground">N/A</span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="max-h-96 overflow-y-auto">
                {loadingTranscript ? (
                  <div className="flex items-center justify-center py-8">
                    <LoadingSpinner size="md" />
                    <span className="ml-2 text-sm text-muted-foreground">Loading transcript...</span>
                  </div>
                ) : transcript ? (
                  <div className="text-sm text-foreground mb-4 whitespace-pre-wrap bg-muted p-4 rounded-lg">
                    {transcript}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground mb-4">
                    <p className="italic">No transcript available for this call.</p>
                  </div>
                )}
                
                {/* Recording Section */}
                {loadingRecording ? (
                  <div className="flex items-center justify-center py-4 mb-4">
                    <LoadingSpinner size="sm" />
                    <span className="ml-2 text-sm text-muted-foreground">Loading recording...</span>
                  </div>
                ) : recordingUrl ? (
                  <div className="mb-4 p-6 bg-card border border-border rounded-lg shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <PlayIcon className="h-5 w-5 text-primary" />
                      <h4 className="font-semibold text-card-foreground">Call Recording</h4>
                    </div>
                    <div className="space-y-3">
                      <audio controls className="w-full h-10 rounded-md">
                        <source src={recordingUrl} type="audio/mpeg" />
                        <source src={recordingUrl} type="audio/wav" />
                        <source src={recordingUrl} type="audio/mp3" />
                        Your browser does not support the audio element.
                      </audio>
                      <div className="flex items-center justify-between pt-2 border-t border-border">
                        <span className="text-sm text-muted-foreground">Available formats: MP3, WAV</span>
                        <a 
                          href={recordingUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 font-medium"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          Open in new tab
                        </a>
                      </div>
                    </div>
                  </div>
                ) : selectedCall?.telnyx_call_control_id ? (
                  <div className="mb-4 p-4 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground italic">No recording available for this call.</p>
                  </div>
                ) : null}
                
                {selectedCall.captured_params && Object.keys(selectedCall.captured_params).length > 0 && (
                  <div className="mt-4 p-6 bg-card border border-success/20 rounded-lg shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <CheckCircleIcon className="h-5 w-5 text-success" />
                      <h4 className="font-semibold text-card-foreground">Captured Information</h4>
                    </div>
                    <div className="bg-success/5 p-4 rounded-md border-l-4 border-success">
                      <div className="space-y-3">
                        {Object.entries(selectedCall.captured_params).map(([key, value]) => (
                          <div key={key} className="flex justify-between items-start">
                            <span className="font-medium text-muted-foreground capitalize text-sm">
                              {key.replace('_', ' ')}:
                            </span>
                            <span className="text-card-foreground font-medium text-sm text-right max-w-xs">{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                
                {selectedCall.reason && (
                  <div className="mt-4 p-6 bg-card border border-destructive/20 rounded-lg shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <ExclamationTriangleIcon className="h-5 w-5 text-destructive" />
                      <h4 className="font-semibold text-card-foreground">Call Outcome</h4>
                    </div>
                    <div className="bg-destructive/5 p-4 rounded-md border-l-4 border-destructive">
                      <p className="text-sm text-card-foreground leading-relaxed">{selectedCall.reason}</p>
                    </div>
                  </div>
                )}
              </div>
              
            <div className="mt-6 flex justify-end">
              <Button
                onClick={() => setShowTranscript(false)}
                variant="outlined"
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CallsPage;